"use client";

import { Modal, ModalContent, ModalHeader, ModalBody } from "@heroui/react";
import { Button } from "@heroui/react";
import { Card, CardBody } from "@heroui/react";
import { Divider } from "@heroui/react";
import { useGetSocialUser, useSocialPayMutations } from "@/hooks/social-pay";
import { useGetCampaignUser } from "@/hooks/challenges";
import { CAMPAIGN_ID, CAMPAIGN_GROUP_ID } from "@/config/campaign";
import { 
  User, 
  LogOut, 
  Twitter, 
  Link2, 
  Unlink, 
  Wallet, 
  Copy, 
  Check,
  X,
  Settings
} from "lucide-react";
import Image from "next/image";
import { useConnection } from "@/contexts/connection-context";
import { WalletAuthButton } from "@/components/WalletAuthButton";
import { BlockiesAvatar } from "@/components/BlockiesAvatar";
import { useState } from "react";

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SocialAccountItemProps {
  icon: React.ComponentType<{ className?: string }>;
  platform: string;
  username?: string | null;
  isLinked: boolean;
  onLink: () => void;
  onUnlink: () => void;
  isLoading?: boolean;
}

const SocialAccountItem = ({ 
  icon: Icon, 
  platform, 
  username, 
  isLinked, 
  onLink, 
  onUnlink, 
  isLoading = false 
}: SocialAccountItemProps) => {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-[#0C1113] rounded-lg">
          {platform === "Discord" ? (
            <Image
              src="https://pulsarmoney.b-cdn.net/rhuna/discord_icon_new.png"
              alt="Discord"
              width={20}
              height={20}
              className="w-5 h-5"
            />
          ) : platform === "X (Twitter)" ? (
            <Image
              src="https://pulsarmoney.b-cdn.net/rhuna/x.png"
              alt="X"
              width={20}
              height={20}
              className="w-5 h-5"
            />
          ) : (
            <Icon className="w-5 h-5 text-gray-300" />
          )}
        </div>
        <div>
          <p className="font-medium text-sm text-white">{platform}</p>
          {isLinked && username ? (
            <p className="text-xs text-gray-400">@{username}</p>
          ) : (
            <p className="text-xs text-gray-500">Not connected</p>
          )}
        </div>
      </div>
      <Button
        size="sm"
        variant={isLinked ? "bordered" : "solid"}
        className={
          isLinked
            ? "border-red-500 text-red-400 hover:bg-red-500/10 rounded-lg"
            : "bg-[#60FF8A] text-black hover:bg-[#60FF8A]/90 rounded-lg"
        }
        onPress={isLinked ? onUnlink : onLink}
        isDisabled={isLoading}
      >
        {isLinked ? "Unlink" : "Link"}
      </Button>
    </div>
  );
};

export const ProfileModal = ({ isOpen, onClose }: ProfileModalProps) => {
  const { isAuthenticated, walletAddress } = useConnection();
  const { data: socialUser, isLoading: socialLoading } = useGetSocialUser();
  const { data: campaignUser } = useGetCampaignUser(CAMPAIGN_ID, CAMPAIGN_GROUP_ID, isAuthenticated);
  const { 
    linkTwitter, 
    linkDiscord, 
    unlinkTwitter, 
    unlinkDiscord 
  } = useSocialPayMutations();
  const [copied, setCopied] = useState(false);

  const handleLinkTwitter = () => {
    linkTwitter.mutate({ redirectUrl: window.location.href });
  };

  const handleLinkDiscord = () => {
    linkDiscord.mutate();
  };

  const handleCopyAddress = () => {
    if (walletAddress) {
      navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatAddress = (address: string) => {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onClose}
      size="2xl"
      placement="center"
      scrollBehavior="inside"
      hideCloseButton
      classNames={{
        backdrop: "bg-black/50 backdrop-blur-sm",
        wrapper: "z-50 flex items-center justify-center",
        base: "bg-[#0C1113] text-white mx-4 my-4 max-h-[90vh] rounded-xl",
        body: "p-0",
        header: "hidden",
      }}
    >
      <ModalContent>
        {() => (
          <>
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-50 w-8 h-8 flex items-center justify-center text-white hover:bg-gray-700 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header with Settings Icon */}
            <div className="flex items-center gap-3 p-6 pb-4">
              <div className="p-2 bg-[#60FF8A]/10 rounded-lg">
                <Settings className="w-5 h-5 text-[#60FF8A]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Account Settings</h2>
              </div>
            </div>
            
            <ModalBody className="p-0">
              {!isAuthenticated ? (
                /* Login Prompt */
                <div className="p-6">
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="flex justify-center mb-6">
                      <div className="w-16 h-16 bg-[#60FF8A]/10 rounded-2xl flex items-center justify-center">
                        <div className="w-8 h-8 bg-[#60FF8A] rounded-lg flex items-center justify-center">
                          <User className="w-5 h-5 text-black" />
                        </div>
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-white text-center mb-3">
                      Sign in to continue
                    </h3>

                    <p className="text-gray-400 text-center mb-8 max-w-sm">
                      Connect your wallet to manage social accounts and access all features
                    </p>

                    <div className="flex justify-center">
                      <WalletAuthButton variant="default" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-6">
                  {/* User Info Card */}
                  <Card className="bg-[#0C1113] border border-gray-800/50">
                    <CardBody className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full overflow-hidden">
                            <BlockiesAvatar
                              seed={walletAddress || 'user'}
                              size={12}
                              scale={4}
                            />
                          </div>
                          <div>
                            <p className="font-semibold text-sm text-white">
                              {walletAddress ? formatAddress(walletAddress) : "User"}
                            </p>
                            {walletAddress && (
                              <div className="flex items-center gap-2 mt-1">
                                <div className="flex items-center gap-1 text-xs text-gray-400">
                                  <Wallet className="w-3 h-3" />
                                  <span>{formatAddress(walletAddress)}</span>
                                </div>
                                <Button
                                  size="sm"
                                  variant="light"
                                  isIconOnly
                                  className="min-w-unit-6 w-6 h-6"
                                  onPress={handleCopyAddress}
                                >
                                  {copied ? (
                                    <Check className="w-3 h-3 text-green-400" />
                                  ) : (
                                    <Copy className="w-3 h-3 text-gray-400" />
                                  )}
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="light"
                          color="danger"
                          className="text-xs px-2 py-1 h-7"
                          onPress={onClose}
                        >
                          Sign Out
                        </Button>
                      </div>
                    </CardBody>
                  </Card>

                  <Divider className="my-4" />

                  {/* Social Accounts Section */}
                  <div>
                    <h4 className="text-sm font-semibold mb-3 text-gray-300">
                      Linked Accounts
                    </h4>
                    {socialLoading ? (
                      <div className="flex justify-center py-8">
                        <div className="w-8 h-8 border-2 border-[#60FF8A] border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <SocialAccountItem
                          icon={Twitter}
                          platform="X (Twitter)"
                          username={socialUser?.twitterMetadata?.username}
                          isLinked={!!socialUser?.twitterMetadata?.username}
                          onLink={handleLinkTwitter}
                          onUnlink={() => unlinkTwitter.mutate()}
                          isLoading={linkTwitter.isPending || unlinkTwitter.isPending}
                        />
                        <SocialAccountItem
                          icon={User}
                          platform="Discord"
                          username={socialUser?.discordMetadata?.username}
                          isLinked={!!socialUser?.discordMetadata?.username}
                          onLink={handleLinkDiscord}
                          onUnlink={() => unlinkDiscord.mutate()}
                          isLoading={linkDiscord.isPending || unlinkDiscord.isPending}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};